import { addMilliseconds, formatDuration, intervalToDuration } from 'date-fns';
import React, { useState } from 'react';
import { useInterval } from 'react-use';
import styled from 'styled-components';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Icon } from '../../../concepts/resources/Icon';
import { Colors } from '../../../ui';
import { DropButton } from '../../../ui/DropButton';
import { useResolve } from '../../Container';
import { Copy, Flexer, Title } from '../../dashboard/elements/LayoutElements';
import { meetingResume } from './MeetingCommands';
import { useActiveMeeting } from './MeetingHooks';
import { ShowIfMeetingHost } from './ShowIfMeetingHost';

const durationFromMs = (ms: number) => {
    const start = new Date();
    const end = addMilliseconds(start, ms);
    return intervalToDuration({ start, end });
};

const formatDurationShort = ({ hours, minutes, seconds }: Duration) => {
    const m = minutes + (hours * 60);
    const s = seconds;
    return [m, s].map(x => x.toString().padStart(2, '0')).join(':');
};

const Numeric = styled.span`
    font-size: 2.5rem;
    font-weight: 600;
    font-family: Inter;
    font-variant-numeric: tabular-nums;
    color: ${Colors.text};
`;

const CoffeeBreakTimeRemaining = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const meeting = useActiveMeeting();
    const [_tick, setTick] = useState(0);
    useInterval(() => setTick(x => x + 1), 100);
    if (!meeting?.alive) return null;
    const start = Date.now();
    const end = meeting.breakStart + meeting.breakLength;
    return <Numeric>
        {end - start < 0 ? '00:00' : formatDurationShort(intervalToDuration({ start, end }))}
    </Numeric>;
};

const CoffeeBreakTimeTotal = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const meeting = useActiveMeeting();
    if (!meeting?.alive) return null;
    return <span>{formatDuration(durationFromMs(meeting.breakLength))}</span>;
};

export const MeetingCoffeeBreakWidget = () => {
    const dispatch = useResolve(CommandDispatcher);
    return <Flexer flex="column" align="center" padding="1.5">
        <Flexer gap=".75" flex="row" center padding=".25">
            <Icon color={Colors.primaryText} src="deqo://icons/meeting/coffee" style={{ transform: 'translateY(-.375rem)' }} />
            <Title size="lg" flush>Coffee Break</Title>
        </Flexer>
        <Copy color={Colors.textLight} flush>
            Let&apos;s take a break and meet back in {open && <CoffeeBreakTimeTotal />}.
        </Copy>
        <Flexer padding="1">
            {open && <CoffeeBreakTimeRemaining />}
        </Flexer>
        <ShowIfMeetingHost>
            <DropButton padding=".25 .75" overflow=".25" onClick={() => dispatch(meetingResume)}>
                <Title weight="400" flush>Resume Meeting</Title>
            </DropButton>
        </ShowIfMeetingHost>
    </Flexer>;
};
